import {useEffect, useRef} from 'react';
import * as echarts from 'echarts';
import 'echarts-gl';
import {compact, flag, healthHex, percent, shortAddress} from '../lib/format';

const ASSETS = `${process.env.PUBLIC_URL}/assets`;
const LINKS_PER_NODE = 3;

// AptosBFT validators are fully meshed; draw a stable sample of those links so the
// globe stays readable (each validator to its nearest few peers).
function sampleLinks(nodes) {
    const pairs = new Set();
    const add = (i, j) => { if (i !== j) pairs.add(i < j ? `${i}-${j}` : `${j}-${i}`); };
    nodes.forEach((a, i) => {
        nodes
            .map((b, j) => ({j, d: (a.lon - b.lon) ** 2 + (a.lat - b.lat) ** 2}))
            .filter(({d}) => d > 1)
            .sort((x, y) => x.d - y.d)
            .slice(0, LINKS_PER_NODE)
            .forEach(({j}) => add(i, j));
    });
    // A few long-haul links so continents are visibly connected too.
    for (let i = 0; i < nodes.length; i += 4) add(i, (i * 7 + 11) % nodes.length);
    return [...pairs].map(key => key.split('-').map(k => [nodes[k].lon, nodes[k].lat]));
}

function tooltip(v) {
    const l = v.location || {};
    return `<div class="tip">
        <div class="tip-title">${v.name || shortAddress(v.address)}</div>
        <div class="tip-sub">${flag(l.countryCode)} ${[l.city, l.country].filter(Boolean).join(', ')}</div>
        <div class="tip-grid">
            <span>Stake</span><b>${compact(v.stake)} APT</b>
            <span>Voting power</span><b>${percent(v.votingShare, 2)}</b>
            <span>Proposals</span><b>${v.successful} ok · ${v.failed} failed</b>
            <span>Provider</span><b>${l.provider || '—'}</b>
        </div>
    </div>`;
}

export default function Globe({validators, focus}) {
    const el = useRef(null);
    const chart = useRef(null);

    useEffect(() => {
        const instance = echarts.init(el.current);
        chart.current = instance;
        const onResize = () => instance.resize();
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
            // echarts-gl refreshes the renderer when a texture finishes loading, which
            // throws if the chart was disposed in the meantime (e.g. on unmount).
            instance.getZr().refresh = () => {};
            instance.dispose();
        };
    }, []);

    useEffect(() => {
        if (chart.current && validators) render(chart.current, validators);
    }, [validators]);

    useEffect(() => {
        const loc = focus?.location;
        if (!chart.current || loc?.latitude == null) return;
        chart.current.setOption({
            globe: {viewControl: {targetCoord: [loc.longitude, loc.latitude], autoRotate: false}},
        });
    }, [focus]);

    return <div ref={el} className="globe-canvas" role="img" aria-label="3D globe of Aptos validator locations" />;
}

function render(chart, validators) {
    const placed = validators.filter(v => v.location?.latitude != null);
    const nodes = placed.map(v => ({lon: v.location.longitude, lat: v.location.latitude}));
    const maxShare = Math.max(...placed.map(v => v.votingShare));

    chart.setOption({
        backgroundColor: 'transparent',
        tooltip: {
            backgroundColor: 'transparent',
            borderWidth: 0,
            padding: 0,
            extraCssText: 'box-shadow:none;',
            formatter: p => (p.seriesIndex === 0 ? tooltip(placed[p.dataIndex]) : ''),
        },
        globe: {
            baseTexture: `${ASSETS}/night.jpg`,
            shading: 'color',
            globeRadius: 88,
            atmosphere: {show: true, offset: 5, color: '#1f6fd1', glowPower: 6, innerGlowPower: 3},
            viewControl: {autoRotate: true, autoRotateSpeed: 4, autoRotateAfterStill: 6, distance: 185, alpha: 25, targetCoord: [15, 42]},
        },
        series: [
            {
                type: 'scatter3D',
                coordinateSystem: 'globe',
                symbolSize: (_, p) => 7 + 13 * Math.sqrt(placed[p.dataIndex].votingShare / maxShare),
                data: placed.map(v => ({
                    value: [v.location.longitude, v.location.latitude, 0],
                    itemStyle: {color: healthHex(v.successRate), opacity: 0.9, borderWidth: 1, borderColor: 'rgba(7, 10, 18, 0.8)'},
                })),
            },
            {
                type: 'lines3D',
                coordinateSystem: 'globe',
                blendMode: 'lighter',
                effect: {show: true, trailWidth: 1.5, trailOpacity: 0.55, trailLength: 0.18, constantSpeed: 60, trailColor: '#7fd8ff'},
                lineStyle: {width: 1, color: '#5ab8ff', opacity: 0.12},
                silent: true,
                data: sampleLinks(nodes),
            },
        ],
    });
}
